import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { apiError } from "@/lib/apiError";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
  Download, FileSignature, Loader2, Search, RefreshCw,
  FileArchive, FileText, Image as ImageIcon, Users, ExternalLink, Copy,
  CheckSquare, Square, X as XIcon, Info, AlertTriangle, Trash2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` } });

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
}

function filenameFromHeader(headers, fallback) {
  const dispo = (headers && headers["content-disposition"]) || "";
  const m = /filename\s*=\s*"?([^";]+)"?/i.exec(dispo);
  return (m && m[1]) || fallback;
}

function triggerBlobDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.rel = "noopener";
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 8000);
}

async function downloadGet(url, fallbackName) {
  try {
    const res = await axios.get(url, { ...auth(), responseType: "blob", timeout: 5 * 60 * 1000 });
    triggerBlobDownload(res.data, filenameFromHeader(res.headers, fallbackName));
    return true;
  } catch (e) {
    let message = "Download failed. Please retry.";
    try { message = JSON.parse(await e.response.data.text()).detail || message; } catch { /* not a JSON response */ }
    toast.error(typeof message === "string" ? message : "Download failed. Please sign in again if your session expired.");
    return false;
  }
}

async function downloadPost(url, body, fallbackName) {
  try {
    const res = await axios.post(url, body, { ...auth(), responseType: "blob", timeout: 5 * 60 * 1000 });
    triggerBlobDownload(res.data, filenameFromHeader(res.headers, fallbackName));
    return true;
  } catch (e) {
    let msg = "Download failed";
    if (e?.response?.data) {
      try {
        const text = await e.response.data.text();
        msg = JSON.parse(text)?.detail || msg;
      } catch { /* not JSON */ }
    }
    toast.error(msg);
    return false;
  }
}

export default function PartyMembership() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [q, setQ] = useState("");
  const [busyCsv, setBusyCsv] = useState(false);
  const [busySelected, setBusySelected] = useState(false);
  const [busyAll, setBusyAll] = useState(false);
  const [batchProgress, setBatchProgress] = useState(null); // { current, total } | null
  const [selected, setSelected] = useState(() => new Set());
  const [exportInfo, setExportInfo] = useState(null); // {total, recommended_batch_size, total_batches}
  const [batchModal, setBatchModal] = useState(null);  // {total, size, nBatches} | null

  const load = async (query = "") => {
    setLoading(true);
    setLoadError("");
    try {
      const params = new URLSearchParams();
      if (query.trim()) params.set("q", query.trim());
      params.set("limit", "500");
      const r = await axios.get(`${API}/membership/onp-wa/list?${params.toString()}`, auth());
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      // Drop selections that aren't in the new result set
      setSelected(prev => {
        const ids = new Set((r.data.items || []).map(it => it.id));
        const next = new Set();
        prev.forEach(id => { if (ids.has(id)) next.add(id); });
        return next;
      });
    } catch (e) {
      const message = apiError(e, "Declarations could not be loaded. Your saved records have not been changed.");
      setLoadError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(""); }, []);

  // Lazily fetch the export-info so the inline hint can show "10 batches expected"
  // without the user having to click anything first.
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/membership/onp-wa/export/info`, auth());
        setExportInfo(r.data);
      } catch { /* non-fatal: hint just won't show */ }
    })();
  }, []);

  const onSearch = (e) => { e.preventDefault(); load(q); };

  const onExportCsv = async () => {
    setBusyCsv(true);
    await downloadGet(`${API}/membership/onp-wa/export/csv`, "onp-wa-declarations.csv");
    setBusyCsv(false);
  };

  const onDownloadPdf = (rid) =>
    downloadGet(`${API}/membership/onp-wa/pdf/${rid}`, `OnP-WA-${rid.slice(0, 8)}.pdf`);

  const onDownloadSig = (rid) =>
    downloadGet(`${API}/membership/onp-wa/signature/${rid}`, `OnP-WA-${rid.slice(0, 8)}.signature.png`);

  // ──────── Selection helpers ────────
  const allVisibleIds = useMemo(() => items.map(it => it.id), [items]);
  const allVisibleSelected = allVisibleIds.length > 0 && allVisibleIds.every(id => selected.has(id));
  const someVisibleSelected = allVisibleIds.some(id => selected.has(id));

  const toggleOne = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleAllVisible = () => {
    setSelected(prev => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        allVisibleIds.forEach(id => next.delete(id));
      } else {
        allVisibleIds.forEach(id => next.add(id));
      }
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  // ──────── Download selected (single ZIP, ≤200 records) ────────
  const [busyDelete, setBusyDelete] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const onDeleteSelected = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    if (deleteConfirm !== "DELETE") return;
    setBusyDelete(true);
    try {
      const r = await axios.post(
        `${API}/membership/onp-wa/delete`,
        { ids, confirm: "DELETE" },
        auth(),
      );
      toast.success(`Deleted ${r.data?.deleted_count ?? ids.length} record${ids.length > 1 ? "s" : ""}`);
      setDeleteOpen(false);
      setDeleteConfirm("");
      clearSelection();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusyDelete(false);
    }
  };

  const onDownloadSelected = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    setBusySelected(true);
    try {
      for (let i = 0; i < ids.length; i += 25) {
        const chunk = ids.slice(i, i + 25);
        const ok = await downloadPost(`${API}/membership/onp-wa/export/zip-selected`, { ids: chunk }, `onp-wa-selected-${i / 25 + 1}.zip`);
        if (!ok) break;
      }
    } finally { setBusySelected(false); }
  };

  // ──────── Download all in sequential batches ────────
  const onDownloadAllBatched = async () => {
    setBusyAll(true);
    try {
      // 1. Ask backend how many batches and what size
      const infoRes = await axios.get(`${API}/membership/onp-wa/export/info`, auth());
      const { total: t, recommended_batch_size: size, total_batches: nBatches } = infoRes.data;
      setExportInfo(infoRes.data);
      if (!t) { toast.info("No declarations to export"); setBusyAll(false); return; }
      if (nBatches === 1) {
        // Small dataset — single ZIP, no scary modal needed
        setBatchProgress({ current: 1, total: 1 });
        const ok = await downloadGet(`${API}/membership/onp-wa/export/zip?batch=1&batch_size=${size}`, "onp-wa-declarations.zip");
        if (ok) toast.success(`Downloaded all ${t} record${t > 1 ? "s" : ""}`);
        setBatchProgress(null);
        setBusyAll(false);
        return;
      }
      // Multiple batches needed → open the styled confirmation modal and wait.
      setBatchModal({ total: t, size, nBatches });
      // The modal's "Start" button calls _runBatchDownload(); "Cancel" calls _cancelBatch().
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not prepare export");
      setBusyAll(false);
    }
  };

  const _cancelBatch = () => {
    setBatchModal(null);
    setBusyAll(false);
  };

  const _runBatchDownload = async (overrideSize) => {
    if (!batchModal) return;
    const t = batchModal.total;
    const size = overrideSize || batchModal.size;
    const nBatches = Math.max(1, Math.ceil(t / size));
    setBatchModal(null);
    try {
      let failed = 0;
      for (let i = 1; i <= nBatches; i++) {
        setBatchProgress({ current: i, total: nBatches });
        const ok = await downloadGet(
          `${API}/membership/onp-wa/export/zip?batch=${i}&batch_size=${size}`,
          `onp-wa-batch-${i}-of-${nBatches}.zip`,
        );
        if (!ok) failed += 1;
        // small pause so the browser actually triggers each save dialog
        await new Promise(r => setTimeout(r, 800));
      }
      if (failed === 0) {
        toast.success(`Downloaded all ${nBatches} ZIP file${nBatches > 1 ? "s" : ""} (${t} records)`);
      } else {
        toast.error(`${failed} of ${nBatches} batches failed — please retry those`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Batch download failed");
    } finally {
      setBatchProgress(null);
      setBusyAll(false);
    }
  };

  const publicUrl = `${window.location.origin}/membership/onp-wa`;
  const copyPublicUrl = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success("Public portal URL copied");
    } catch {
      toast.error("Could not copy — please copy the URL manually");
    }
  };

  return (
    <div className="space-y-6" data-testid="party-membership-admin">
      {loadError && <div role="alert" data-testid="membership-load-error" className="border border-red-200 bg-red-50 text-red-900 p-4 rounded space-y-2">
        <p>{loadError}</p><Button data-testid="membership-load-retry" variant="outline" onClick={() => load(q)}>Retry</Button>
      </div>}
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-[#1E3A8A]" style={{ fontFamily: "'Playfair Display', serif" }}>
            Party Membership Declarations
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            One Nation Western Australia — WAEC FD25 declarations. Tick rows to download in batches, or pull the whole archive.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <a
            href="/membership/onp-wa" target="_blank" rel="noreferrer noopener"
            data-testid="open-public-portal-btn"
            className="inline-flex items-center justify-center gap-2 rounded-md border border-[#1E3A8A]/30 px-3 py-2 text-sm font-medium text-[#1E3A8A] hover:bg-[#1E3A8A]/5 transition-colors"
          >
            <ExternalLink className="w-4 h-4" /> Open public portal
          </a>
          <Button data-testid="copy-portal-url-btn" variant="outline" onClick={copyPublicUrl}>
            <Copy className="w-4 h-4 mr-2" /> Copy URL
          </Button>
          <Button data-testid="export-csv-btn" variant="outline" onClick={onExportCsv} disabled={busyCsv}>
            {busyCsv ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
            Export CSV
          </Button>
          <Button data-testid="export-all-zip-btn" onClick={onDownloadAllBatched} disabled={busyAll}
            className="bg-[#FF6B35] hover:bg-[#E15829] text-white">
            {busyAll
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {batchProgress ? `Batch ${batchProgress.current}/${batchProgress.total}…` : "Working…"}</>
              : <><FileArchive className="w-4 h-4 mr-2" /> Download all (batched ZIPs)</>}
          </Button>
        </div>
      </div>

      {/* Public portal banner */}
      <div className="bg-[#FFF6F0] border border-[#FFE0CC] rounded-sm p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="public-portal-banner">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-[#E15829]">Public sign-up portal</div>
          <div className="text-sm text-gray-700 mt-0.5">
            Share this link with prospective members — it serves the official WAEC FD25 declaration form.
          </div>
          <div className="mt-1.5 inline-block bg-white border border-[#FFE0CC] rounded px-2.5 py-1 font-mono text-xs text-[#1E3A8A]" data-testid="public-portal-url">
            {publicUrl}
          </div>
        </div>
        <a
          href="/membership/onp-wa" target="_blank" rel="noreferrer noopener"
          className="inline-flex items-center gap-2 rounded-md bg-[#FF6B35] hover:bg-[#E15829] text-white px-4 py-2 text-sm font-semibold transition-colors"
          data-testid="open-public-portal-banner-btn"
        >
          <ExternalLink className="w-4 h-4" /> Open portal
        </a>
      </div>

      {/* Summary card */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-white rounded-sm border border-gray-200 p-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-sm bg-[#1E3A8A]/10 flex items-center justify-center">
            <Users className="w-5 h-5 text-[#1E3A8A]" />
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wider">Total declarations</div>
            <div className="text-2xl font-semibold text-gray-900" data-testid="declarations-total">{total}</div>
          </div>
        </div>
        <div className="bg-white rounded-sm border border-gray-200 p-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-sm bg-[#FF6B35]/10 flex items-center justify-center">
            <FileSignature className="w-5 h-5 text-[#FF6B35]" />
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wider">Form</div>
            <div className="text-sm font-medium text-gray-900">WAEC FD25 v1.2</div>
          </div>
        </div>
        <div className="bg-white rounded-sm border border-gray-200 p-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-sm bg-emerald-100 flex items-center justify-center">
            <FileArchive className="w-5 h-5 text-emerald-700" />
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wider">Selected</div>
            <div className="text-sm font-medium text-gray-900" data-testid="selected-count">
              {selected.size === 0 ? "None selected" : `${selected.size} record${selected.size > 1 ? "s" : ""}`}
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <form onSubmit={onSearch} className="flex gap-2 items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            data-testid="search-input"
            value={q} onChange={e => setQ(e.target.value)}
            placeholder="Search by name, email, postcode or suburb…"
            className="pl-9"
          />
        </div>
        <Button type="submit" variant="outline" data-testid="search-btn">Search</Button>
        <Button type="button" variant="ghost" onClick={() => { setQ(""); load(""); }} data-testid="clear-btn">
          Clear
        </Button>
        <Button type="button" variant="ghost" onClick={() => load(q)} data-testid="refresh-btn">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </form>

      {/* Inline batch hint — visible BEFORE clicking, so the user is never surprised. */}
      {exportInfo && exportInfo.total_batches > 1 && (
        <div
          className="bg-blue-50 border border-blue-200 rounded-md px-4 py-3 flex items-start gap-3"
          data-testid="batch-hint-banner"
        >
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900 leading-relaxed">
            <strong>Heads up:</strong> there are <strong>{exportInfo.total}</strong> declarations on file.
            Because that's too large for a single download to be reliable, clicking
            <strong> "Download all (batched ZIPs)"</strong> will produce
            <strong> {exportInfo.total_batches} separate ZIP files</strong> of about
            <strong> {exportInfo.recommended_batch_size} records each</strong>,
            downloaded one after another. Make sure your browser allows multiple downloads from this site.
          </div>
        </div>
      )}

      {/* Selection toolbar — appears when at least one row is ticked */}
      {selected.size > 0 && (
        <div className="bg-[#1E3A8A]/5 border border-[#1E3A8A]/20 rounded-md px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="selection-toolbar">
          <div className="text-sm text-[#1E3A8A] font-medium">
            {selected.size} record{selected.size > 1 ? "s" : ""} selected
            {selected.size > 200 && (
              <span className="ml-2 text-xs text-red-600 font-normal">(max 200 per download — please reduce)</span>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="ghost" onClick={clearSelection} data-testid="clear-selection-btn">
              <XIcon className="w-4 h-4 mr-1" /> Clear selection
            </Button>
            <Button
              size="sm"
              onClick={onDownloadSelected}
              disabled={busySelected || selected.size > 200}
              className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white"
              data-testid="download-selected-btn"
            >
              {busySelected ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileArchive className="w-4 h-4 mr-2" />}
              Download selected ({selected.size})
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => { setDeleteConfirm(""); setDeleteOpen(true); }}
              disabled={busyDelete}
              className="border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800"
              data-testid="delete-selected-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete selected ({selected.size})
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="declarations-table">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-left text-xs uppercase tracking-wider text-gray-500">
                <th className="px-3 py-3 w-10">
                  <button
                    type="button"
                    onClick={toggleAllVisible}
                    className="text-[#1E3A8A] hover:text-[#1E3A8A]/80 disabled:opacity-30"
                    disabled={items.length === 0}
                    data-testid="select-all-btn"
                    aria-label="Select all visible"
                    title={allVisibleSelected ? "Deselect all visible" : "Select all visible"}
                  >
                    {allVisibleSelected
                      ? <CheckSquare className="w-4 h-4" />
                      : someVisibleSelected
                        ? <CheckSquare className="w-4 h-4 opacity-60" />
                        : <Square className="w-4 h-4" />}
                  </button>
                </th>
                <th className="px-4 py-3">Submitted</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Phone / Mobile</th>
                <th className="px-4 py-3">Suburb</th>
                <th className="px-4 py-3">Postcode</th>
                <th className="px-4 py-3 text-right">Files</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-gray-500">
                  <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
                </td></tr>
              )}
              {!loading && !loadError && items.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-gray-400" data-testid="empty-state">
                  No declarations found.
                </td></tr>
              )}
              {!loading && items.map((d) => {
                const m = d.member || {};
                const phone = [m.phone, m.mobile].filter(Boolean).join(" / ") || "—";
                const checked = selected.has(d.id);
                return (
                  <tr key={d.id} className={`hover:bg-gray-50 ${checked ? "bg-[#1E3A8A]/5" : ""}`} data-testid={`row-${d.id}`}>
                    <td className="px-3 py-3">
                      <button
                        type="button"
                        onClick={() => toggleOne(d.id)}
                        className="text-[#1E3A8A] hover:opacity-80"
                        data-testid={`row-checkbox-${d.id}`}
                        aria-label={checked ? "Deselect row" : "Select row"}
                      >
                        {checked ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4 text-gray-400" />}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{fmtDate(d.submitted_at)}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{m.full_name || `${m.given_names || ""} ${m.surname || ""}`.trim() || "—"}</div>
                      <div className="text-xs text-gray-400">DOB: {m.date_of_birth || "—"}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{m.email || "—"}</td>
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{phone}</td>
                    <td className="px-4 py-3 text-gray-700">{m.locality || "—"}</td>
                    <td className="px-4 py-3 text-gray-700">{m.postcode || "—"}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => onDownloadPdf(d.id)} data-testid={`pdf-${d.id}`}>
                          <Download className="w-4 h-4 mr-1" /> PDF
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => onDownloadSig(d.id)} data-testid={`sig-${d.id}`}>
                          <ImageIcon className="w-4 h-4 mr-1" /> Sig
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {!loading && items.length > 0 && items.length < total && (
          <div className="px-4 py-2 text-xs text-gray-500 border-t border-gray-100">
            Showing {items.length} of {total}. Use search to narrow down, or click "Download all (batched ZIPs)" to grab the full archive.
          </div>
        )}
      </div>

      {/* Styled "multiple downloads" confirmation modal */}
      {batchModal && (
        <BatchConfirmModal
          info={batchModal}
          onCancel={_cancelBatch}
          onStart={_runBatchDownload}
        />
      )}

      {/* Typed-confirmation modal for irreversible bulk delete */}
      {deleteOpen && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
          data-testid="delete-confirm-modal"
          onClick={() => { if (!busyDelete) { setDeleteOpen(false); setDeleteConfirm(""); } }}
        >
          <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Permanently delete {selected.size} declaration{selected.size > 1 ? "s" : ""}?
                </h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  This removes the PDF, signature image, and member details from the database. <strong>This cannot be undone.</strong>
                </p>
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800 space-y-2">
              <div>Make sure you have downloaded the ZIP of these records first if you need a copy.</div>
              <div>Type <strong className="font-mono">DELETE</strong> below to confirm.</div>
            </div>

            <Input
              autoFocus
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              placeholder="Type DELETE to confirm"
              className="font-mono"
              data-testid="delete-confirm-input"
              disabled={busyDelete}
            />

            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="ghost"
                onClick={() => { setDeleteOpen(false); setDeleteConfirm(""); }}
                disabled={busyDelete}
                data-testid="delete-modal-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={onDeleteSelected}
                disabled={busyDelete || deleteConfirm !== "DELETE"}
                className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
                data-testid="delete-modal-confirm"
              >
                {busyDelete
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Deleting…</>
                  : <><Trash2 className="w-4 h-4 mr-2" /> Delete {selected.size} record{selected.size > 1 ? "s" : ""}</>}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Batch confirmation modal with a customisable records-per-ZIP picker.
// ─────────────────────────────────────────────────────────────────────────────
function BatchConfirmModal({ info, onCancel, onStart }) {
  const [size, setSize] = useState(info.size);
  const nBatches = Math.max(1, Math.ceil(info.total / size));
  const approxMb = Math.round((size * 0.88) * 10) / 10; // ~880KB per PDF
  const RISK = (() => {
    return { tone: "emerald", label: "Smaller downloads", note: "Choose fewer records for slower connections." };
  })();
  const toneClasses = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    amber:   "bg-amber-50 border-amber-200 text-amber-800",
    rose:    "bg-rose-50 border-rose-200 text-rose-800",
  }[RISK.tone];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
      data-testid="batch-confirm-modal"
      onClick={onCancel}
    >
      <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Multiple ZIP files will be downloaded</h3>
            <p className="text-sm text-gray-500 mt-0.5">Due to the large number of declarations on file.</p>
          </div>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-md p-4 text-sm text-gray-700 space-y-3">
          <div className="flex justify-between">
            <span className="text-gray-500">Total declarations</span>
            <span className="font-semibold text-gray-900">{info.total}</span>
          </div>

          <div>
            <div className="flex justify-between items-center mb-1.5">
              <label htmlFor="batch-size-input" className="text-gray-500">Records per ZIP</label>
              <span className="font-mono text-xs text-gray-600">{size} (~{approxMb} MB)</span>
            </div>
            <input
              id="batch-size-input"
              type="range"
              min="5"
              max="25"
              step="5"
              value={size}
              onChange={(e) => setSize(Number(e.target.value))}
              className="w-full accent-[#1E3A8A]"
              data-testid="batch-size-slider"
            />
            <div className="flex justify-between text-[10px] text-gray-400 mt-0.5 px-0.5">
              <span>5</span><span>10</span><span>15</span><span>20</span><span>25</span>
            </div>
          </div>

          <div className="flex justify-between">
            <span className="text-gray-500">ZIP files you'll receive</span>
            <span className="font-semibold text-[#1E3A8A]" data-testid="batch-count-preview">{nBatches}</span>
          </div>

          <div className={`text-xs rounded px-3 py-2 border ${toneClasses}`} data-testid="batch-risk-badge">
            <strong>{RISK.label}:</strong> {RISK.note}
          </div>
        </div>

        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
          <strong>Before you start:</strong> Your browser may ask whether to allow multiple downloads from this site. Click <strong>Allow</strong> when prompted, otherwise only the first ZIP will save.
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onCancel} data-testid="batch-modal-cancel">
            Cancel
          </Button>
          <Button
            onClick={() => onStart(size)}
            className="bg-[#FF6B35] hover:bg-[#E15829] text-white"
            data-testid="batch-modal-start"
          >
            <FileArchive className="w-4 h-4 mr-2" /> Start download ({nBatches} ZIP{nBatches > 1 ? "s" : ""})
          </Button>
        </div>
      </div>
    </div>
  );
}
